import React, {
    Component,
    createRef,
} from 'react';

import {
    ErrorMessage,
    FieldArray,
    Formik,
} from 'formik';
import {addLocale} from 'primereact/api';
import {Button} from 'primereact/button';
import {Calendar} from 'primereact/calendar';
import {Checkbox} from 'primereact/checkbox';
import {Column} from 'primereact/column';
import {DataTable} from 'primereact/datatable';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {InputNumber} from 'primereact/inputnumber';
import {InputText} from 'primereact/inputtext';
import {InputTextarea} from 'primereact/inputtextarea';
import {Menu} from 'primereact/menu';
import {MultiSelect} from 'primereact/multiselect';
import {Tag} from 'primereact/tag';
import {Toast} from 'primereact/toast';
import {Toolbar} from 'primereact/toolbar';
import {classNames} from 'primereact/utils';
import {connect} from 'react-redux';
import {
    I18n,
    Translate,
} from 'react-redux-i18n';
import * as Yup from 'yup';

import {
    Box,
    Typography,
} from '@material-ui/core';

import {Order} from '../helpers/order';
import {Permission} from '../helpers/permission';
import {
    copyToClipboard,
    DetailsInfoRow,
    formatCustomDate,
    getApiKey,
    getFormErrorMessage,
    isFormFieldInvalid,
    localeEnUSOptions,
    localeFrFROptions,
} from '../helpers/utils';
import ApiService from '../services/ApiService';
import PaymentLinkService from '../services/PaymentLinkService';
import CodeQRCode from './qr-code/CodeQRCode';

class PaymentLink extends Component {
    emptyElement = {
        merchantPaysFees: 'true',
        merchantSetAmount: 'true',
        amount: null,
        maxPayments: null,
        maxQuantity: null,
        currencyCode: '',
        reason: '',
        allowedProviders: [],
        customAttributes: [],
        durationType: null,
        maxQuantityPerUser: null,
        type: '',
        durationCalendar: null,
        langCode: '',
        advancedSettings: false,
        askWhatsappNumber: false,
        askPhoneNumber: false,
        askBillingAddress: false,
        askShippingAddress: false,
        limitLinks: false,
        limitQuantity: false,
        addCustomAttributes: false,
        askEmail: false,
    };

    constructor(props) {
        super(props);
        this.formikProps = {
            enableReinitialize: true,
            initialValues: this.emptyElement,
            validationSchema: Yup.object().shape({
                type: Yup.string().required(I18n.t('FIELD_IS_REQUIRED')),
                langCode: Yup.string().required(I18n.t('FIELD_IS_REQUIRED')),
                merchantPaysFees: Yup.string().required(I18n.t('FIELD_IS_REQUIRED')),
                merchantSetAmount: Yup.string().required(I18n.t('FIELD_IS_REQUIRED')),
                advancedSettings: Yup.bool().notRequired(),
                limitLinks: Yup.bool().notRequired(),
                limitQuantity: Yup.bool().notRequired(),
                addCustomAttributes: Yup.bool().notRequired(),
                currencyCode: Yup.string().required(I18n.t('CURRENCY_CODE_IS_REQUIRED')),
                amount: Yup.number()
                    .nullable()
                    .when('merchantSetAmount', {
                        is: "true",
                        then: Yup.number().required(I18n.t('AMOUNT_IS_REQUIRED')).typeError(I18n.t('AMOUNT_IS_REQUIRED')),
                        otherwise: Yup.number().nullable()
                    }),
                maxPayments: Yup.number()
                    .nullable()
                    .when('limitLinks', {
                        is: true,
                        then: Yup.number()
                            .required(I18n.t('FIELD_IS_REQUIRED'))
                            .typeError(I18n.t('FIELD_IS_REQUIRED')),
                        otherwise: Yup.number().nullable()
                    })
                    .test(
                        'is-valid-payments',
                        I18n.t('INVALID_MAX_PAYMENT_LINKS_VALUE'),
                        function (value) {
                            const {id, usedPayments} = this.parent;
                            if (id && value != null && usedPayments != null) {
                                return value > usedPayments;
                            }
                            return true;
                        }
                    ),
                maxQuantity: Yup.number()
                    .nullable()
                    .when('limitQuantity', {
                        is: true,
                        then: Yup.number()
                            .required(I18n.t('FIELD_IS_REQUIRED'))
                            .typeError(I18n.t('FIELD_IS_REQUIRED')),
                        otherwise: Yup.number().nullable()
                    })
                    .test(
                        'is-valid-quantity',
                        I18n.t('INVALID_MAX_PAYMENT_QUANTITY_VALUE'),
                        function (value) {
                            const {id, usedQuantity} = this.parent;
                            if (id && value != null && usedQuantity != null) {
                                return value > usedQuantity;
                            }
                            return true;
                        }
                    ),
                maxQuantityPerUser: Yup.number()
                    .nullable()
                    .when('limitQuantity', {
                        is: true,
                        then: Yup.number()
                            .integer(I18n.t('FIELD_MUST_BE_AN_INTEGER'))
                            .positive(I18n.t('FIELD_MUST_BE_POSITIVE'))
                            .required(I18n.t('FIELD_IS_REQUIRED'))
                            .typeError(I18n.t('FIELD_IS_REQUIRED')),
                        otherwise: Yup.number().nullable()
                    }),
                reason: Yup.string().required(I18n.t('FIELD_IS_REQUIRED')),
                durationType: Yup.number()
                    .nullable()
                    .when('type', {
                        is: 'DISPOSABLE',
                        then: Yup.number().required(I18n.t('LINK_EXPIRATION_IS_REQUIRED')).typeError(I18n.t('LINK_EXPIRATION_IS_REQUIRED')),
                        otherwise: Yup.number().nullable()
                    }),
                durationCalendar: Yup.date()
                    .nullable()
                    .when('durationType', {
                        is: -2,
                        then: Yup.date().required(I18n.t('EXPIRATION_DATE_IS_REQUIRED')).typeError(I18n.t('EXPIRATION_DATE_IS_REQUIRED')),
                        otherwise: Yup.date().nullable(),
                    }),
                allowedProviders: Yup.array()
                    .of(Yup.string())
                    .min(1, I18n.t('AT_LEAST_ONE_PROVIDER_REQUIRED'))
                    .required(I18n.t('FIELD_IS_REQUIRED')),
                customAttributes: Yup.array().of(
                    Yup.object().shape({
                        label: Yup.string().required(I18n.t('FIELD_IS_REQUIRED')),
                        type: Yup.string().required(I18n.t('FIELD_IS_REQUIRED')),
                        required: Yup.bool().notRequired(),
                    })
                ).when('addCustomAttributes', {
                    is: true,
                    then: (schema) => schema.min(1, I18n.t('FIELD_IS_REQUIRED')),
                    otherwise: (schema) => schema.notRequired(),
                })
            }), onSubmit: (element) => {
                this.saveElement(element)
            }
        };

        addLocale('fr', localeFrFROptions);
        addLocale('en', localeEnUSOptions);
        this.formikRef = React.createRef();
        this.menuRefs = new Map();

        this.endPoint = "/v1/payments/link"

        this.state = {
            loading: false,
            first: 0,
            pageSize: 5,
            totalRecords: 0,
            elements: [],
            elementDialog: false,
            deleteElementDialog: false,
            detailsElementDialog: false,
            element: this.emptyElement,
            selectedElement: null,
            menuModel: [],
            loadingDialog: false,
            searchFilterValue: '',
            sortFilterValue: null,
            sortFilterOrder: 1
        };

        this.elementService = new PaymentLinkService();
        this.apiService = new ApiService();
        this.leftToolbarTemplate = this.leftToolbarTemplate.bind(this);
        this.rightToolbarTemplate = this.rightToolbarTemplate.bind(this);
        this.loadData = this.loadData.bind(this);
        this.customSort = this.customSort.bind(this);
        this.loadTypes = this.loadTypes.bind(this);
        this.loadLanguages = this.loadLanguages.bind(this);
        this.renderFooter = this.renderFooter.bind(this);
        this.activateElement = this.activateElement.bind(this);

        this.openNew = this.openNew.bind(this);
        this.onPage = this.onPage.bind(this);
        this.saveElement = this.saveElement.bind(this);
        this.editElement = this.editElement.bind(this);
        this.confirmDeleteElement = this.confirmDeleteElement.bind(this);
        this.deleteElement = this.deleteElement.bind(this);
        this.confirmDeleteSelected = this.confirmDeleteSelected.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.hideElementDialog = this.hideElementDialog.bind(this);
    }

    componentDidMount() {
        this.loadCurrencies();
        this.loadDurationTypes();
        this.loadMerchantPaysFeesOptions();
        this.loadMerchantSetAmountOptions();
        this.loadCustomFieldTypes();
        this.loadPaymentOptions();
        this.loadTypes();
        this.loadLanguages();
        this.loadData({
            businessIdentifier: this.props.business?.current?.identifier,
            isLive: this.props.business?.isBusinessProduction,
            start: 0,
            pageSize: this.state.pageSize,
            sortField: this.state.sortFilterValue,
            order: this.state.sortFilterOrder,
            searchValue: this.state.searchFilterValue,
        });
    }

    loadData({businessIdentifier, isLive, start, pageSize, sortField, order, searchValue}) {
        this.setState({
            loading: true
        })
        let searchParams = [
            {field: 'business_identifier', value: businessIdentifier},
            {field: 'is_live', value: isLive}
        ]
        if (!!searchValue) {
            searchParams.push({field: 'search', value: searchValue});
        }

        let sortOrder = order === -1 ? Order.ASC : Order.DESC;
        this.elementService.getPage(
            start,
            pageSize,
            sortField,
            sortOrder,
            searchParams
        ).then(response => {
            this.setState({
                elements: response.data.content.list ? response.data.content.list : [],
                totalRecords: response.data.content.totalCount,
                loading: false
            })
        }).catch(error => {
            this.setState({
                loading: false
            });
        });
    }


    componentDidUpdate(prevProps, prevState, snapshot) {
        const localeChanged = prevProps.locale !== this.props.locale;
        const businessChanged = prevProps.business !== this.props.business;

        if (localeChanged || businessChanged) {
            this.loadCurrencies();
            this.loadDurationTypes();
            this.loadMerchantPaysFeesOptions();
            this.loadMerchantSetAmountOptions();
            this.loadCustomFieldTypes();
            this.loadPaymentOptions();
            this.loadTypes();
            this.loadLanguages();
            this.loadData({
                businessIdentifier: this.props.business?.current?.identifier,
                isLive: this.props.business?.isBusinessProduction,
                start: this.state.first,
                pageSize: this.state.pageSize,
                sortField: this.state.sortFilterValue,
                order: this.state.sortFilterOrder,
                searchValue: this.state.searchFilterValue
            });
        }
    }

    loadTypes() {
        const permissions = this.props.permissions;
        const types = [];
        if (this.props.business?.current?.type && this.props.business?.current?.type !== "DEVELOPER") {
            if (permissions?.includes(Permission.PERMISSION_MERCHANT_CREATE_DISPOSABLE_PAYMENT_LINK)) {
                types.push({name: I18n.t('PAYMENT_DISPOSABLE_LINK_DESCRIPTION'), code: 'DISPOSABLE'});
            }
            if (permissions?.includes(Permission.PERMISSION_MERCHANT_CREATE_BUSINESS_PAYMENT_LINK)) {
                types.push({name: I18n.t('PAYMENT_BUSINESS_LINK_DESCRIPTION'), code: 'BUSINESS'});
            }
        }
        this.types = types;
    }

    loadLanguages() {
        const languages = [];
        languages.push({name: I18n.t('FRENCH'), code: 'fr'});
        languages.push({name: I18n.t('ENGLISH'), code: 'en'});
        this.languages = languages;
    }

    loadMerchantSetAmountOptions() {
        const merchantSetAmountOptions = [];
        merchantSetAmountOptions.push({name: I18n.t('PAYMENT_OPTION_MERCHANT_SET_AMOUNT_DESCRIPTION'), code: "true"});
        merchantSetAmountOptions.push({name: I18n.t('PAYMENT_OPTION_CLIENT_SET_AMOUNT_DESCRIPTION'), code: "false"});
        this.merchantSetAmountOptions = merchantSetAmountOptions;
    }

    loadCustomFieldTypes() {
        const customFieldTypes = [];
        customFieldTypes.push({name: I18n.t('NUMBER_CUSTOM_FIELD'), code: "NUMBER"});
        customFieldTypes.push({name: I18n.t('INTEGER_CUSTOM_FIELD'), code: "INTEGER"});
        customFieldTypes.push({name: I18n.t('TEXT_CUSTOM_FIELD'), code: "TEXT"});
        this.customFieldTypes = customFieldTypes;
    }

    loadMerchantPaysFeesOptions() {
        const merchantPaysFeesOptions = [];
        merchantPaysFeesOptions.push({name: I18n.t('PAYMENT_OPTION_MERCHANT_PAYS_FEES_DESCRIPTION'), code: "true"});
        merchantPaysFeesOptions.push({name: I18n.t('PAYMENT_OPTION_CLIENT_PAYS_FEES_DESCRIPTION'), code: "false"});
        this.merchantPaysFeesOptions = merchantPaysFeesOptions;
    }

    loadCurrencies() {
        const currencies = [];
        const list = this.props.settings?.CURRENCIES;
        if (list && list.length > 0) {
            list.forEach(currency => {
                currencies.push({name: I18n.t(currency + '_DESCRIPTION'), code: currency});
            });
        }
        this.currencies = currencies;
    }

    loadDurationTypes() {
        this.durations = [
            {name: I18n.t('30_MINUTES_HINT'), code: 30 * 60},
            {name: I18n.t('1_HOUR_HINT'), code: 60 * 60},
            {name: I18n.t('24_HOUR_HINT'), code: 24 * 60 * 60},
            //{ name: I18n.t('1_WEEK_HINT'), code: 7 * 24 * 60 * 60 },
            //{ name: I18n.t('1_MONT_HINT'), code: 30 * 24 * 60 * 60 },
            {name: I18n.t('UNLIMITED_HINT'), code: -1},
            {name: I18n.t('CUSTOM_HINT'), code: -2}
        ];
    }

    loadPaymentOptions() {
        const paymentOptions = [];
        const list = this.props.settings?.PROVIDERS;
        if (list && list.length > 0) {
            list.forEach(provider => {
                paymentOptions.push({label: I18n.t(provider + '_PROVIDER_DESCRIPTION'), value: provider});
            });
        }
        this.paymentOptions = paymentOptions;
    }


    onPage(event) {
        this.setState({loading: true});
        const {page, first, rows} = event;
        this.loadData({
            businessIdentifier: this.props.business?.current?.identifier,
            isLive: this.props.business?.isBusinessProduction,
            start: page,
            pageSize: rows,
            sortField: this.state.sortFilterValue,
            order: this.state.sortFilterOrder,
            searchValue: this.state.searchFilterValue
        });
        this.setState({
            loading: false,
            first
        })
    }

    openNew() {
        let initialValues = {...this.emptyElement};
        initialValues.maxQuantityPerUser = this.props.business?.current?.maxQuantityPerUser
        initialValues.currencyCode = this.props.business?.current?.currencyCode
        this.formikProps.initialValues = initialValues;
        this.setState({
            elementDialog: true
        });
    }

    hideElementDialog() {
        this.formikProps.initialValues = this.emptyElement
        this.setState({
            elementDialog: false,
            deleteElementDialog: false,
            activateElementDialog: false,
            detailsElementDialog: false,
            element: this.emptyElement
        });
    }

    renderFooter = () => {
        return (
            <>
                <Button type="button" loading={this.state.loadingDialog} label={I18n.t('CANCEL')}
                        icon="pi pi-times" className="p-button-text p-button-help"
                        onClick={this.hideElementDialog}/>
                <Button type="submit" loading={this.state.loadingDialog}
                        label={this.state.element?.identifier ? I18n.t('EDIT') : I18n.t('SAVE')}
                        icon={this.state.element?.identifier ? "pi pi-pencil" : "pi pi-check"} className="p-button-text"
                        onClick={() => this.formikRef.current?.submitForm()}
                />
            </>
        );
    }

    renderDetailFooter = () => {
        return (
            <>
                <Button type="button" loading={this.state.loadingDialog} label={I18n.t('CLOSE')}
                        icon="pi pi-times" className="p-button-text p-button-help"
                        onClick={this.hideElementDialog}/>
            </>
        );
    }

    saveElement(element) {
        let elements = [...this.state.elements];
        this.setState({
            loadingDialog: true,
        });

        element.duration = element.durationType
        if (element.durationType === -1) {
            element.duration = null;
        } else if (element.durationType === -2) {
            const now = new Date();
            element.duration = Math.floor((element.durationCalendar - now.getTime()) / 1000);
        }

        if (element.merchantSetAmount === "false") {
            element.amount = null;
        }

        if (element.limitLinks === false) {
            element.maxPayments = null;
        }

        if (element.limitQuantity === false) {
            element.maxQuantity = null;
            element.maxQuantityPerUser = null;
        }

        if (element.identifier) { //may be update
            this.apiService.patch(this.endPoint + "/" + element.identifier, getApiKey(this.props.business, true), element)
                .then((response) => {
                    const index = this.findIndexById(element.paymentId);
                    elements[index] = response.data.content;
                    this.setState({
                        elements,
                        elementDialog: false,
                        loadingDialog: false,
                        element: this.emptyElement,
                    });
                    this.formikProps.initialValues = this.emptyElement;
                    this.toast.show({
                        severity: 'success', summary: I18n.t('UPDATE'), detail: I18n.t('UPDATE_SUCCESSFUL'), life: 6000
                    });
                })
                .catch(error => {
                    this.setState({
                        loadingDialog: false,
                    });
                    this.toast.show({
                        severity: 'error',
                        summary: I18n.t('UPDATE'),
                        detail: I18n.t(error.response.data.code),
                        life: 6000
                    });
                });
        } else { //may be creation
            this.apiService.post(this.endPoint, getApiKey(this.props.business, true), element)
                .then((response) => {
                    elements.unshift(response.data.content);
                    this.setState({
                        elements,
                        elementDialog: false,
                        loadingDialog: false,
                        element: this.emptyElement,
                        totalRecords: this.state.totalRecords + 1,
                    });
                    this.formikProps.initialValues = this.emptyElement;
                    this.toast.show({
                        severity: 'success', summary: I18n.t('ADD'), detail: I18n.t('ADD_SUCCESSFUL'), life: 6000
                    });
                })
                .catch(error => {
                    this.setState({
                        loadingDialog: false,
                    });
                    this.toast.show({
                        severity: 'error',
                        summary: I18n.t(error.response.data.code),
                        detail: I18n.t(error.response.data.code + '_MESSAGE'),
                        life: 6000
                    });
                });
        }
    }

    createInitialValues = (element) => {
        let initialValues = {...this.emptyElement};
        initialValues.paymentId = element.paymentId
        initialValues.merchantPaysFees = element.merchantPaysFees.toString()
        initialValues.amount = element.initialAmount
        initialValues.currencyCode = element.currencyCode
        initialValues.reason = element.reason
        initialValues.allowedProviders = element.acceptedProviders
        initialValues.type = element.type
        initialValues.identifier = element.identifier
        initialValues.langCode = element.langCode
        initialValues.merchantSetAmount = element.initialAmount ? "true" : "false"
        initialValues.askEmail = element.askEmail
        initialValues.askPhoneNumber = element.askPhoneNumber
        initialValues.askWhatsappNumber = element.askWhatsappNumber
        initialValues.maxQuantityPerUser = element.maxQuantityPerUser
        initialValues.askShippingAddress = element.askShippingAddress
        initialValues.askBillingAddress = element.askBillingAddress
        initialValues.maxPayments = element.maxPayments
        initialValues.usedPayments = element.usedPayments
        initialValues.maxQuantity = element.maxQuantity
        initialValues.usedQuantity = element.usedQuantity
        initialValues.customAttributes = element.customAttributes ?? []
        if (!!element.maxPayments) {
            initialValues.limitLinks = true
        }
        if (!!element.maxQuantity) {
            initialValues.limitQuantity = true
        }
        if (!!element.customAttributes && element.customAttributes.length > 0) {
            initialValues.addCustomAttributes = true
        }
        if (element.advancedSettings === true) {
            initialValues.advancedSettings = true
        }
        return initialValues;
    }

    editElement(element) {
        this.formikProps.initialValues = this.createInitialValues(element);
        this.setState({
            elementDialog: true
        });
    }

    duplicateElement(element) {
        let initialValues = this.createInitialValues(element);
        initialValues.paymentId = null
        initialValues.identifier = null
        this.formikProps.initialValues = initialValues
        this.setState({
            elementDialog: true
        });
    }

    showDetailsElement(element) {
        this.setState({
            element, detailsElementDialog: true
        });
    }

    confirmDeleteElement(element) {
        this.setState({
            element, deleteElementDialog: true
        });
    }

    confirmActivateElement(element) {
        this.setState({
            element, activateElementDialog: true
        });
    }

    deleteElement() {
        this.setState({
            loadingDialog: true,
        });
        this.elementService.delete(this.state.element.identifier)
            .then(() => {
                let elements = this.state.elements.filter(val => val.identifier !== this.state.element.identifier);
                this.setState({
                    elements,
                    deleteElementDialog: false,
                    element: this.emptyElement,
                    totalRecords: this.state.totalRecords - 1,
                    loadingDialog: false
                });
                this.toast.show({
                    severity: 'success', summary: I18n.t('DELETION'), detail: I18n.t('DELETION_SUCCESSFUL'), life: 6000
                });
            })
            .catch(error => {
                this.setState({
                    deleteElementDialog: false, element: this.emptyElement, loadingDialog: false
                });
                this.toast.show({
                    severity: 'error', summary: I18n.t('DELETION'), detail: I18n.t(error.response.data.code), life: 6000
                });
            });
    }

    activateElement() {
        this.setState({
            loadingDialog: true,
        });
        this.elementService.manageActivation(this.state.element.identifier)
            .then((response) => {
                const index = this.findIndexById(this.state.element.paymentId);
                let elements = [...this.state.elements];
                elements[index] = response.data.content;
                this.setState({
                    elements, activateElementDialog: false, loadingDialog: false, element: null,
                });
                this.toast.show({
                    severity: 'success', summary: I18n.t('UPDATE'), detail: I18n.t('UPDATE_SUCCESSFUL'), life: 6000
                });
            })
            .catch(error => {
                this.setState({
                    loadingDialog: false,
                });
                this.toast.show({
                    severity: 'error',
                    summary: I18n.t('UPDATE'),
                    detail: I18n.t(error.response.data.code),
                    life: 6000
                });
            });
    }

    findIndexById(paymentId) {
        let index = -1;
        for (let i = 0; i < this.state.elements.length; i++) {
            if (this.state.elements[i].paymentId === paymentId) {
                index = i;
                break;
            }
        }
        console.log("index")
        return index;
    }

    confirmDeleteSelected() {
        this.setState({deleteElementsDialog: true});
    }

    onSearch() {
        this.loadData({
            businessIdentifier: this.props.business?.current?.identifier,
            isLive: this.props.business?.isBusinessProduction,
            start: this.state.first,
            pageSize: this.state.pageSize,
            sortField: this.state.sortFilterValue,
            order: this.state.sortFilterOrder,
            searchValue: this.state.searchFilterValue
        });
    }

    customSort(event) {
        const {sortField, sortOrder} = event;
        this.setState({
            sortFilterValue: sortField,
            sortFilterOrder: sortOrder
        })
        this.loadData({
            businessIdentifier: this.props.business?.current?.identifier,
            isLive: this.props.business?.isBusinessProduction,
            start: this.state.first,
            pageSize: this.state.pageSize,
            sortField: sortField,
            order: sortOrder,
            searchValue: this.state.searchFilterValue
        });
    }

    leftToolbarTemplate() {
        return (<React.Fragment>
            <Button label={I18n.t('ADD_DATA')} icon="pi pi-plus" className="p-button-success p-mr-2"
                    onClick={this.openNew}
                    disabled={!(this.props.business?.current?.type && this.props.business?.current?.type !== "DEVELOPER")}/>
        </React.Fragment>)
    }

    getMinDate() {
        const now = new Date();
        now.setMinutes(now.getMinutes() + 30);
        return now;
    }

    handleCopy = async (textToCopy, titleCode) => {
        try {
            await copyToClipboard(textToCopy);
            this.toast.show({severity: 'success', detail: I18n.t(titleCode), life: 6000});
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    rightToolbarTemplate() {
        return (<React.Fragment>
            <Button label={I18n.t('EXPORT_DATA')} icon="pi pi-external-link" className="p-button-help"
                    onClick={this.exportCSV}/>
        </React.Fragment>)
    }

    typeBodyTemplate = (data) => {
        return (
            <>
                <span className="p-column-title">{I18n.t('PAYMENT_LINK_TYPE')}</span>
                {I18n.t(data.type + '_LINK')}
            </>
        );
    };

    businessNameBodyTemplate = (data) => {
        return (
            <>
                <span className="p-column-title">{I18n.t('BUSINESS_NAME')}</span>
                {data.businessName}
            </>
        );
    };

    reasonBodyTemplate = (data) => {
        return (
            <>
                <span className="p-column-title">{I18n.t('REASON')}</span>
                {data.reason}
            </>
        );
    };

    initialAmountBodyTemplate = (data) => {
        return (
            <>
                <span className="p-column-title">{I18n.t('AMOUNT')}</span>
                {data.initialAmount}
            </>
        );
    };

    statusBodyTemplate = (data) => {
        let component;
        if (data.status === 'DISABLED') {
            component = <Tag value={I18n.t(data.status)} severity="danger" outlined/>
        } else if (data.status === 'EXPIRED') {
            component = <Tag value={I18n.t(data.status)} severity="warning" outlined/>
        } else {
            component = <Tag value={I18n.t(data.status)} severity="success" outlined/>
        }
        return (
            <>
                <span className="p-column-title">{I18n.t('STATUS')}</span>
                {component}
            </>
        );
    };

    createdOnBodyTemplate = (data) => {
        return (
            <>
                <span className="p-column-title">{I18n.t('CREATION_DATE')}</span>
                {formatCustomDate(data.createdOn, this.props.locale)}
            </>
        );
    };

    currencyCodeBodyTemplate = (data) => {
        return (
            <>
                <span className="p-column-title">{I18n.t('CURRENCY_CODE')}</span>
                {I18n.t(data.currencyCode + '_DESCRIPTION')}
            </>
        );
    };


    handleMenuToggle = (event, rowData) => {
        const ref = this.menuRefs.get(rowData.identifier);
        if (ref && ref.current) {
            ref.current.toggle(event);
        }
    };

    actionBodyTemplate = (rowData) => {
        // Create a ref per row if not already created
        if (!this.menuRefs.has(rowData.identifier)) {
            this.menuRefs.set(rowData.identifier, createRef());
        }

        let activateLabel = rowData.active === true ? I18n.t('DEACTIVATE') : I18n.t('ACTIVATE')
        let activateIcon = rowData.active === true ? 'pi pi-times' : 'pi pi-check'

        const menuItems = [
            {
                label: I18n.t('COPY_URL'),
                icon: 'pi pi-link',
                command: () => this.handleCopy(rowData.url, 'URL_COPIED')
            },
            {
                label: I18n.t('COPY_ID'),
                icon: 'pi pi-copy',
                command: () => this.handleCopy(rowData.identifier, 'ID_COPIED')
            },
            {
                label: I18n.t('DETAILS'),
                icon: 'pi pi-info-circle',
                command: () => this.showDetailsElement(rowData)
            }
        ];

        if (rowData.status !== "EXPIRED") {
            menuItems.push(
                {
                    label: I18n.t('EDIT'),
                    icon: 'pi pi-pencil',
                    command: () => this.editElement(rowData)
                },
                {
                    label: activateLabel,
                    icon: activateIcon,
                    command: () => this.confirmActivateElement(rowData)
                });

            if (rowData.usedPayments === 0 && rowData.usedQuantity === 0) {
                menuItems.push({
                    label: I18n.t('DELETE'),
                    icon: 'pi pi-trash',
                    command: () => this.confirmDeleteElement(rowData)
                });
            }
        }

        menuItems.push(
            {
                label: I18n.t('DUPLICATE'),
                icon: 'pi pi-clone',
                command: () => this.duplicateElement(rowData)
            });

        return (
            <div>
                <Button
                    icon="pi pi-ellipsis-h"
                    className="p-button-text"
                    onClick={(e) => this.handleMenuToggle(e, rowData)}
                />
                <Menu model={menuItems} popup ref={this.menuRefs.get(rowData.identifier)}/>
            </div>
        );
    };

    render() {
        const header = (<div className="table-header">
            <h5 className="p-m-0">{I18n.t('PAYMENT_LINKS')}</h5>
            <span className="p-input-icon-left">
            <div className="p-inputgroup">
                <InputText placeholder={I18n.t('SEARCH_BY_BUSINESS_NAME_OR_REASON')}
                           onChange={e => this.setState({searchFilterValue: e.target.value})}
                           value={this.state.searchFilterValue}
                           style={{width: '400px'}}
                />
                <Button icon="pi pi-search" onClick={this.onSearch}/>
            </div>
        </span>
        </div>);

        const deleteElementDialogFooter = (<React.Fragment>
            <Button loading={this.state.loadingDialog} label={I18n.t('NO')} icon="pi pi-times"
                    className="p-button-text" onClick={this.hideElementDialog}/>
            <Button loading={this.state.loadingDialog} label={I18n.t('YES')} icon="pi pi-check"
                    className="p-button-text" onClick={this.deleteElement}/>
        </React.Fragment>);

        const activateElementDialogFooter = (<React.Fragment>
            <Button loading={this.state.loadingDialog} label={I18n.t('NO')} icon="pi pi-times"
                    className="p-button-text" onClick={this.hideElementDialog}/>
            <Button loading={this.state.loadingDialog} label={I18n.t('YES')} icon="pi pi-check"
                    className="p-button-text" onClick={this.activateElement}/>
        </React.Fragment>);


        return (<div className="crud-demo">
            <Toast ref={(el) => this.toast = el}/>
            <div className="card">
                <Toolbar className="p-mb-4" left={this.leftToolbarTemplate} right={this.rightToolbarTemplate}/>
                <DataTable ref={(el) => this.dt = el} value={this.state.elements}
                           lazy
                           emptyMessage={I18n.t('NO_ITEMS_FOUND')}
                           selectionMode="single"
                           dataKey="id" paginator loading={this.state.loading}
                           rows={this.state.pageSize} totalRecords={this.state.totalRecords}
                           first={this.state.first} onPage={this.onPage}
                           paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                           currentPageReportTemplate={I18n.t('PAGE_REPORT_TEMPLATE')}
                           sortMode="single"
                           onSort={this.customSort}
                           sortField={this.state.sortFilterValue}
                           sortOrder={this.state.sortFilterOrder}
                           responsive="true"
                           className="p-datatable-products"
                           showGridlines={false}
                           header={header}
                >
                    <Column field="type" header={I18n.t('PAYMENT_LINK_TYPE')} sortable body={this.typeBodyTemplate}/>
                    <Column header={I18n.t('STATUS')} body={this.statusBodyTemplate}/>
                    {!this.props.business?.current?.identifier && (
                        <Column field="businessName" header={I18n.t('BUSINESS_NAME')} sortable
                                body={this.businessNameBodyTemplate}/>
                    )}
                    <Column field="createdOn" header={I18n.t('CREATION_DATE')} body={this.createdOnBodyTemplate}/>
                    <Column field="initialAmount" header={I18n.t('AMOUNT')} body={this.initialAmountBodyTemplate}/>
                    <Column field="currencyCode" header={I18n.t('CURRENCY_CODE')} body={this.currencyCodeBodyTemplate}/>
                    <Column field="reason" header={I18n.t('REASON')} body={this.reasonBodyTemplate}/>
                    <Column headerStyle={{width: '4rem'}} body={this.actionBodyTemplate}/>

                </DataTable>

            </div>

            <Dialog visible={this.state.elementDialog} header={I18n.t('DETAILS')} modal
                    breakpoints={{'960px': '75vw', '640px': '90vw'}}
                    closable={false} onHide={this.hideElementDialog} footer={this.renderFooter()}
            >
                <Formik {...this.formikProps} innerRef={this.formikRef}>
                    {props => {
                        //console.log(props.values)
                        return <form onKeyDown={(event) => event.keyCode === 13 && event.preventDefault()}
                                     onSubmit={props.handleSubmit} className="p-fluid">

                            <div className="p-grid">
                                <div
                                    className={classNames('p-card p-col-12', {' p-md-6': props.values.advancedSettings === true})}>
                                    <DetailsInfoRow
                                        label={I18n.t('BUSINESS_NAME')}
                                        value={this.props.business?.current?.name}
                                    />

                                    <DetailsInfoRow
                                        label="Mode"
                                        value={
                                            <Tag
                                                value={this.props.business?.isBusinessProduction ? I18n.t('LIVE') : 'Test'}
                                                severity={this.props.business?.isBusinessProduction ? 'success' : 'warning'}
                                                style={{fontSize: '12px', color: 'white'}}
                                            />
                                        }
                                    />

                                    {this.state.element?.identifier && <DetailsInfoRow
                                        label={I18n.t('PAYMENT_LINK_TYPE')}
                                        value={I18n.t(this.state.element.type + '_LINK')}
                                    />}

                                    {!this.state.element?.identifier &&
                                        <div className="p-field" style={{marginTop: '10px'}}>
                                <span className="p-float-label">
                                    <Dropdown
                                        name="type"
                                        id="type"
                                        value={props.values.type}
                                        onChange={props.handleChange}
                                        options={this.types}
                                        optionLabel="name"
                                        optionValue="code"
                                        className={classNames({'p-invalid': isFormFieldInvalid(props, 'type')})}
                                    />
                                    <label htmlFor="type">{I18n.t('PAYMENT_LINK_TYPE')}</label>
                                </span>
                                            {getFormErrorMessage(props, 'type')}
                                        </div>}

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                <span className="p-float-label">
                                    <Dropdown
                                        name="langCode"
                                        id="langCode"
                                        value={props.values.langCode}
                                        onChange={props.handleChange}
                                        options={this.languages}
                                        optionLabel="name"
                                        optionValue="code"
                                        className={classNames({'p-invalid': isFormFieldInvalid(props, 'langCode')})}
                                    />
                                    <label htmlFor="type">{I18n.t('CLIENT_DISPLAY_LANGUAGE')}</label>
                                </span>
                                        {getFormErrorMessage(props, 'langCode')}
                                    </div>

                                    {props.values.type === "DISPOSABLE" && <div style={{marginTop: '10px'}}>
                                        <div className="p-inputgroup">
                                    <span className="p-float-label">
                                       <Dropdown
                                           name="durationType"
                                           id="durationType"
                                           value={props.values.durationType}
                                           onChange={props.handleChange}
                                           options={this.durations}
                                           optionLabel="name"
                                           optionValue="code"
                                           className={classNames({'p-invalid': isFormFieldInvalid(props, 'durationType')})}
                                       />
                                        <label htmlFor="duration"
                                               className={classNames({'p-error': isFormFieldInvalid(props, 'durationType')})}>{I18n.t('CHOOSE_PAYMENT_LINK_DURATION')}*</label>
                                    </span>
                                            {props.values.durationType === -2 && <span className="p-float-label">
                                        <Calendar
                                            id="durationCalendar"
                                            value={props.values.durationCalendar
                                                ? new Date(props.values.durationCalendar)
                                                : this.getMinDate()
                                            }
                                            onChange={(event) => props.setFieldValue('durationCalendar', event.value)}
                                            className={classNames({'p-invalid': isFormFieldInvalid(props, 'durationCalendar')})}
                                            dateFormat="dd/mm/yy"
                                            locale={this.props.locale?.split('-')[0]}
                                            minDate={this.getMinDate()}
                                            showTime
                                            hideOnDateTimeSelect
                                            showIcon/>
                                        <label htmlFor="durationCalendar"
                                               className={classNames({'p-error': isFormFieldInvalid(props, 'durationCalendar')})}>{I18n.t('EXPIRATION_DATE')}*</label>
                                    </span>}
                                        </div>
                                        <div>{getFormErrorMessage(props, 'durationType')}</div>
                                        <div>{getFormErrorMessage(props, 'durationCalendar')}</div>
                                    </div>}

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                <span className="p-float-label">
                                    <InputTextarea
                                        rows={1}
                                        autoResize
                                        name="reason"
                                        value={props.values.reason}
                                        onChange={props.handleChange}
                                        id="reason"
                                        className={classNames({'p-invalid': isFormFieldInvalid(props, 'reason')})}
                                    />
                                    <label htmlFor="reason"
                                           className={classNames({'p-error': isFormFieldInvalid(props, 'reason')})}>{I18n.t('PAYMENT_REASON')}*</label>
                                     <small id="reason2" className="p-d-block">{I18n.t('PAYMENT_REASON_HINT')}</small>
                                </span>
                                        {getFormErrorMessage(props, 'reason')}
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                <span className="p-float-label">
                                   <Dropdown
                                       name="merchantSetAmount"
                                       id="merchantSetAmount"
                                       value={props.values.merchantSetAmount}
                                       onChange={props.handleChange}
                                       options={this.merchantSetAmountOptions}
                                       optionLabel="name"
                                       optionValue="code"
                                       className={classNames({'p-invalid': isFormFieldInvalid(props, 'merchantSetAmount')})}
                                   />
                                   <label htmlFor="merchantSetAmount">{I18n.t('WHO_ENTER_AMOUNT_PLACEHOLDER')}</label>
                                </span>
                                        {getFormErrorMessage(props, 'merchantSetAmount')}
                                    </div>

                                    <div style={{marginTop: '10px'}}>
                                        <div className="p-inputgroup">
                                            {props.values.merchantSetAmount === "true" &&
                                                <span className="p-float-label">
                                        <InputNumber
                                            name="amount"
                                            value={props.values.amount}
                                            showButtons mode="decimal"
                                            locale={this.props.locale}
                                            minFractionDigits={1}
                                            maxFractionDigits={2}
                                            min={1}
                                            onValueChange={(e) => props.setFieldValue("amount", e.target.value)}
                                            id="amount"
                                            className={classNames({'p-invalid': isFormFieldInvalid(props, 'amount')})}
                                        />
                                        <label htmlFor="amount"
                                               className={classNames({'p-error': isFormFieldInvalid(props, 'amount')})}>{I18n.t('AMOUNT')}*</label>
                                    </span>}
                                            <span className="p-float-label">
                                       <Dropdown
                                           name="currencyCode"
                                           id="currencyCode"
                                           value={props.values.currencyCode}
                                           onChange={props.handleChange}
                                           options={this.currencies}
                                           optionLabel="name"
                                           optionValue="code"
                                           className={classNames({'p-invalid': isFormFieldInvalid(props, 'currencyCode')})}
                                       />
                                        <label htmlFor="currencyCode"
                                               className={classNames({'p-error': isFormFieldInvalid(props, 'amount')})}>{I18n.t('CHOOSE_CURRENCY')}*</label>
                                    </span>
                                        </div>
                                        <div>{getFormErrorMessage(props, 'currencyCode')}</div>
                                        <div>{getFormErrorMessage(props, 'amount')}</div>
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                <span className="p-float-label">
                                   <Dropdown
                                       name="merchantPaysFees"
                                       id="merchantPaysFees"
                                       value={props.values.merchantPaysFees}
                                       onChange={props.handleChange}
                                       options={this.merchantPaysFeesOptions}
                                       optionLabel="name"
                                       optionValue="code"
                                       className={classNames({'p-invalid': isFormFieldInvalid(props, 'merchantPaysFees')})}
                                   />
                                   <label htmlFor="merchantPaysFees">{I18n.t('WHO_PAY_FEES_PLACEHOLDER')}</label>
                                </span>
                                        {getFormErrorMessage(props, 'merchantPaysFees')}
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-float-label">
                                            <MultiSelect
                                                options={this.paymentOptions}
                                                optionLabel="label"
                                                display="chip"
                                                className="w-full md:w-30rem payment-link-multiselect"
                                                name="allowedProviders"
                                                value={props.values.allowedProviders}
                                                onChange={props.handleChange}
                                                id="allowedProviders"
                                            />
                                            <label htmlFor="allowedProviders"
                                                   className={classNames({'p-error': isFormFieldInvalid(props, 'allowedProviders')})}>{I18n.t('SELECT_PROVIDER_MESSAGE')}*</label>
                                        </span>
                                        {getFormErrorMessage(props, 'allowedProviders')}
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="advancedSettings"
                                                name="advancedSettings"
                                                checked={props.values.advancedSettings}
                                                onChange={(e) => {
                                                    props.setFieldValue("advancedSettings", e.checked);
                                                }}
                                            />
                                            <label htmlFor="advancedSettings">{I18n.t('SHOW_ADVANCED_SETTINGS')}</label>
                                        </span>
                                    </div>
                                </div>
                                {props.values.advancedSettings === true && <div
                                    className={classNames('p-card p-col-12', {' p-md-6': props.values.advancedSettings === true})}
                                >

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="askPhoneNumber"
                                                name="askPhoneNumber"
                                                checked={props.values.askPhoneNumber}
                                                onChange={(e) => {
                                                    props.setFieldValue("askPhoneNumber", e.checked);
                                                }}
                                            />
                                            <label htmlFor="askPhoneNumber">{I18n.t('ASK_PHONE_NUMBER')}</label>
                                        </span>
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="askEmail"
                                                name="askEmail"
                                                checked={props.values.askEmail}
                                                onChange={(e) => {
                                                    props.setFieldValue("askEmail", e.checked);
                                                }}
                                            />
                                            <label htmlFor="askEmail">{I18n.t('ASK_EMAIL')}</label>
                                        </span>
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="askWhatsappNumber"
                                                name="askWhatsappNumber"
                                                checked={props.values.askWhatsappNumber}
                                                onChange={(e) => {
                                                    props.setFieldValue("askWhatsappNumber", e.checked);
                                                }}
                                            />
                                            <label htmlFor="askWhatsappNumber">{I18n.t('ASK_WHATSAPP_NUMBER')}</label>
                                        </span>
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="askBillingAddress"
                                                name="askBillingAddress"
                                                checked={props.values.askBillingAddress}
                                                onChange={(e) => {
                                                    props.setFieldValue("askBillingAddress", e.checked);
                                                }}
                                            />
                                            <label htmlFor="askBillingAddress">{I18n.t('ASK_BILLING_ADDRESS')}</label>
                                        </span>
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="askShippingAddress"
                                                name="askShippingAddress"
                                                checked={props.values.askShippingAddress}
                                                onChange={(e) => {
                                                    props.setFieldValue("askShippingAddress", e.checked);
                                                }}
                                            />
                                            <label htmlFor="askShippingAddress">{I18n.t('ASK_SHIPPING_ADDRESS')}</label>
                                        </span>
                                    </div>

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="limitLinks"
                                                name="limitLinks"
                                                checked={props.values.limitLinks}
                                                onChange={(e) => {
                                                    props.setFieldValue("limitLinks", e.checked);
                                                    if (e.checked === true) {
                                                        props.setFieldValue("limitQuantity", false);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="limitLinks">{I18n.t('LIMIT_NUMBER_OF_LINKS')}</label>
                                        </span>
                                    </div>

                                    {props.values.limitLinks === true &&
                                        <div className="p-px-3">
                                            <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <InputNumber
                                                        name="maxPayments"
                                                        value={props.values.maxPayments}
                                                        showButtons
                                                        min={1}
                                                        onValueChange={(e) => props.setFieldValue("maxPayments", e.target.value)}
                                                        id="maxPayments"
                                                        className={classNames({'p-invalid': isFormFieldInvalid(props, 'maxPayments')})}
                                                    />
                                                    <label htmlFor="maxPayments"
                                                           className={classNames({'p-error': isFormFieldInvalid(props, 'maxPayments')})}>{I18n.t('LIMIT')}*</label>
                                                </span>

                                                {props.values.identifier &&
                                                    <span className="p-float-label">
                                                    <InputNumber
                                                        name="usedPayments"
                                                        value={props.values.usedPayments}
                                                        disabled
                                                        id="usedPayments"
                                                    />
                                                    <label
                                                        htmlFor="usedPayments">{I18n.t('USED_NUMBER')}</label>
                                                </span>}
                                            </div>
                                            <div>{getFormErrorMessage(props, 'maxPayments')}</div>
                                        </div>}

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="limitQuantity"
                                                name="limitQuantity"
                                                checked={props.values.limitQuantity}
                                                onChange={(e) => {
                                                    props.setFieldValue("limitQuantity", e.checked);
                                                    if (e.checked === true) {
                                                        props.setFieldValue("limitLinks", false);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="limitQuantity">{I18n.t('SET_QUANTITY')}</label>
                                        </span>
                                    </div>

                                    {props.values.limitQuantity === true &&
                                        <>
                                            <div className="p-px-3">
                                                <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <InputNumber
                                                        name="maxQuantity"
                                                        value={props.values.maxQuantity}
                                                        showButtons
                                                        min={1}
                                                        onValueChange={(e) => props.setFieldValue("maxQuantity", e.target.value)}
                                                        id="maxQuantity"
                                                        className={classNames({'p-invalid': isFormFieldInvalid(props, 'maxQuantity')})}
                                                    />
                                                    <label htmlFor="maxQuantity"
                                                           className={classNames({'p-error': isFormFieldInvalid(props, 'maxQuantity')})}>{I18n.t('PRODUCT_QUANTITY')}*</label>
                                                </span>

                                                    {props.values.identifier &&
                                                        <span className="p-float-label">
                                                    <InputNumber
                                                        name="usedQuantity"
                                                        value={props.values.usedQuantity}
                                                        disabled
                                                        id="usedQuantity"
                                                    />
                                                    <label
                                                        htmlFor="usedQuantity">{I18n.t('USED_NUMBER')}</label>
                                                </span>}
                                                </div>
                                                <div>{getFormErrorMessage(props, 'maxQuantity')}</div>
                                            </div>
                                            <div className="p-p-3">
                                                <span className="p-float-label">
                                                    <InputNumber
                                                        name="maxQuantityPerUser"
                                                        value={props.values.maxQuantityPerUser}
                                                        showButtons
                                                        min={1}
                                                        onValueChange={(e) => props.setFieldValue("maxQuantityPerUser", e.target.value)}
                                                        id="maxQuantityPerUser"
                                                        className={classNames({'p-invalid': isFormFieldInvalid(props, 'maxQuantityPerUser')})}
                                                    />
                                                    <label htmlFor="maxQuantityPerUser"
                                                           className={classNames({'p-error': isFormFieldInvalid(props, 'maxQuantityPerUser')})}>{I18n.t('MAX_QUANTITY_PER_USER')}*</label>
                                                </span>
                                                <div>{getFormErrorMessage(props, 'maxQuantityPerUser')}</div>
                                            </div>
                                        </>}

                                    <div className="p-field" style={{marginTop: '10px'}}>
                                        <span className="p-field-checkbox">
                                            <Checkbox
                                                inputId="addCustomAttributes"
                                                name="addCustomAttributes"
                                                checked={props.values.addCustomAttributes}
                                                onChange={(e) => {
                                                    props.setFieldValue("addCustomAttributes", e.checked);
                                                    if (e.checked === false) {
                                                        props.setFieldValue("customAttributes", [])
                                                    }
                                                }}
                                            />
                                            <label
                                                htmlFor="addCustomAttributes">{I18n.t('ADD_CUSTOM_ATTRIBUTES')}</label>
                                        </span>
                                    </div>

                                    {props.values.addCustomAttributes === true &&
                                        <FieldArray name="customAttributes">
                                            {({remove, push}) => (
                                                <div className="p-px-3">
                                                    {props.values.customAttributes.map((field, index) => (
                                                        <div key={index} className="p-fluid p-card p-m-3 p-p-3">

                                                            <div className="p-field" style={{marginTop: '10px'}}>
                                                            <span className="p-float-label">
                                                                <InputText
                                                                    id={`customAttributes[${index}].label`}
                                                                    name={`customAttributes[${index}].label`}
                                                                    value={field.label}
                                                                    onChange={props.handleChange}
                                                                />
                                                                 <label
                                                                     htmlFor={`customAttributes[${index}].label`}>{I18n.t('LABEL_NAME')}</label>
                                                                <small className="p-error">
                                                                    <ErrorMessage
                                                                        name={`customAttributes[${index}].label`}/>
                                                                </small>
                                                            </span>
                                                            </div>

                                                            <div className="p-field" style={{marginTop: '10px'}}>
                                                            <span className="p-float-label">
                                                                <Dropdown
                                                                    optionLabel="name"
                                                                    optionValue="code"
                                                                    options={this.customFieldTypes}
                                                                    value={field.type}
                                                                    onChange={(e) => props.setFieldValue(`customAttributes[${index}].type`, e.value)}
                                                                    id={`customAttributes[${index}].type`}
                                                                    name={`customAttributes[${index}].type`}
                                                                />
                                                                 <label
                                                                     htmlFor={`customAttributes[${index}].type`}>Type</label>
                                                                <small className="p-error">
                                                                    <ErrorMessage
                                                                        name={`customAttributes[${index}].type`}/>
                                                                </small>
                                                            </span>
                                                            </div>

                                                            <div className="p-field" style={{marginTop: '10px'}}>
                                                            <span className="p-field-checkbox">
                                                                <Checkbox
                                                                    id={`customAttributes[${index}].required`}
                                                                    name={`customAttributes[${index}].required`}
                                                                    checked={field.required}
                                                                    onChange={(e) => props.setFieldValue(`customAttributes[${index}].required`, e.checked)}
                                                                />
                                                                 <label
                                                                     htmlFor={`customAttributes[${index}].required`}>{I18n.t('MARK_AS_REQUIRED')}</label>
                                                                <small className="p-error">
                                                                    <ErrorMessage
                                                                        name={`customAttributes[${index}].required`}/>
                                                                </small>
                                                            </span>
                                                            </div>

                                                            <Button
                                                                type="button"
                                                                icon="pi pi-trash"
                                                                className="p-button-danger mt-2"
                                                                onClick={() => remove(index)}
                                                                label={I18n.t('REMOVE_CUSTOM_FIELD')}
                                                            />
                                                        </div>
                                                    ))}

                                                    <Button
                                                        type="button"
                                                        icon="pi pi-plus"
                                                        onClick={() => push({label: '', type: '', required: false})}
                                                        label={I18n.t('ADD_CUSTOM_FIELD')}
                                                        className="p-button-outlined p-button-info"
                                                    />
                                                    {typeof props.errors.customAttributes === 'string' && props.touched.customAttributes && (
                                                        <small
                                                            className="p-error">{props.errors.customAttributes}</small>
                                                    )}
                                                </div>
                                            )}
                                        </FieldArray>}
                                </div>}
                            </div>
                        </form>
                    }}
                </Formik>
            </Dialog>

            <Dialog visible={this.state.deleteElementDialog} style={{width: '450px'}}
                    header={I18n.t('CONFIRMATION')} modal footer={deleteElementDialogFooter} closable={false}
                    onHide={this.hideElementDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle p-mr-3" style={{fontSize: '2rem'}}/>
                    {this.state.element &&
                        <Translate dangerousHTML value="DELETE_ONE_WARNING_REASON"
                                   name={this.state.element.reason + "&nbsp;(" + this.state.element.businessName + ")"}/>}
                </div>
            </Dialog>

            <Dialog visible={this.state.activateElementDialog} style={{width: '450px'}}
                    header={I18n.t('CONFIRMATION')} modal footer={activateElementDialogFooter} closable={false}
                    onHide={this.hideElementDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle p-mr-3" style={{fontSize: '2rem'}}/>
                    {this.state.element && this.state.element.active === false &&
                        <Translate dangerousHTML value="ACTIVATE_ONE_WARNING_REASON"
                                   name={this.state.element.reason + "&nbsp;(" + this.state.element.businessName + ")"}/>}
                    {this.state.element && this.state.element.active === true &&
                        <Translate dangerousHTML value="DEACTIVATE_ONE_WARNING_REASON"
                                   name={this.state.element.reason + "&nbsp;(" + this.state.element.businessName + ")"}/>}
                </div>
            </Dialog>

            <Dialog visible={this.state.detailsElementDialog} style={{width: '500px'}} header={I18n.t('DETAILS')} modal
                    className="p-fluid" closable={false} onHide={this.hideElementDialog}
                    footer={this.renderDetailFooter()}>
                {this.state.element && <div>
                    <DetailsInfoRow
                        label={I18n.t('BUSINESS_NAME')}
                        value={this.props.business?.current?.name}
                    />

                    <DetailsInfoRow
                        label="Mode"
                        value={
                            <Tag
                                value={this.props.business?.isBusinessProduction === true ? I18n.t('LIVE') : 'Test'}
                                severity={this.props.business?.isBusinessProduction === true ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    <DetailsInfoRow
                        label={I18n.t('PAYMENT_LINK_TYPE')}
                        value={I18n.t(this.state.element.type + '_LINK')}
                    />

                    {this.state.element.initialAmount && <DetailsInfoRow
                        label={I18n.t('AMOUNT')}
                        value={this.state.element.initialAmount}
                    />}

                    <DetailsInfoRow
                        label={I18n.t('CURRENCY_CODE')}
                        value={I18n.t(this.state.element.currencyCode + '_DESCRIPTION')}
                    />

                    <DetailsInfoRow
                        label={I18n.t('ACTIVE')}
                        value={
                            <Tag
                                value={this.state.element.active ? I18n.t('YES') : I18n.t('NO')}
                                severity={this.state.element.active ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    <DetailsInfoRow
                        label={I18n.t('REASON')}
                        value={this.state.element.reason}
                    />

                    {this.state.element.status && <DetailsInfoRow
                        label={I18n.t('STATUS')}
                        value={I18n.t(this.state.element.status)}
                    />}

                    <DetailsInfoRow
                        label={I18n.t('CREATION_DATE')}
                        value={formatCustomDate(this.state.element.createdOn, this.props.locale)}
                    />

                    {this.state.element.expireAt && <DetailsInfoRow
                        label={I18n.t('EXPIRATION_DATE')}
                        value={formatCustomDate(this.state.element.expireAt, this.props.locale)}
                    />}

                    <DetailsInfoRow
                        label={I18n.t('YOU_PAY_PLATFORM_FEES')}
                        value={
                            <Tag
                                value={this.state.element.merchantPaysFees ? I18n.t('YES') : I18n.t('NO')}
                                severity={this.state.element.merchantPaysFees ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    <DetailsInfoRow
                        label={I18n.t('ASK_PHONE_NUMBER')}
                        value={
                            <Tag
                                value={this.state.element.askPhoneNumber === true ? I18n.t('YES') : I18n.t('NO')}
                                severity={this.state.element.askPhoneNumber === true ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    <DetailsInfoRow
                        label={I18n.t('ASK_EMAIL')}
                        value={
                            <Tag
                                value={this.state.element.askEmail === true ? I18n.t('YES') : I18n.t('NO')}
                                severity={this.state.element.askEmail === true ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    <DetailsInfoRow
                        label={I18n.t('ASK_WHATSAPP_NUMBER')}
                        value={
                            <Tag
                                value={this.state.element.askWhatsappNumber === true ? I18n.t('YES') : I18n.t('NO')}
                                severity={this.state.element.askWhatsappNumber === true ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    <DetailsInfoRow
                        label={I18n.t('ASK_BILLING_ADDRESS')}
                        value={
                            <Tag
                                value={this.state.element.askBillingAddress === true ? I18n.t('YES') : I18n.t('NO')}
                                severity={this.state.element.askBillingAddress === true ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    <DetailsInfoRow
                        label={I18n.t('ASK_SHIPPING_ADDRESS')}
                        value={
                            <Tag
                                value={this.state.element.askShippingAddress === true ? I18n.t('YES') : I18n.t('NO')}
                                severity={this.state.element.askShippingAddress === true ? 'success' : 'warning'}
                                style={{fontSize: '12px', color: 'white'}}
                            />
                        }
                    />

                    {this.state.element.maxPayments != null && <DetailsInfoRow
                        label={I18n.t('LINK_LIMIT_NUMBER')}
                        value={this.state.element.maxPayments}
                    />}

                    {this.state.element.maxPayments != null && <DetailsInfoRow
                        label={I18n.t('NUMBER_OF_USED_LINKS')}
                        value={this.state.element.usedPayments}
                    />}

                    {this.state.element.maxQuantity != null && <DetailsInfoRow
                        label={I18n.t('QUANTITY_LIMIT_NUMBER')}
                        value={this.state.element.maxQuantity}
                    />}

                    {this.state.element.maxQuantity != null && <DetailsInfoRow
                        label={I18n.t('NUMBER_OF_USED_QUANTITY')}
                        value={this.state.element.usedQuantity}
                    />}

                    {this.state.element.customAttributes && <DetailsInfoRow
                        label={I18n.t('CUSTOM_ATTRIBUTES')}
                        value={
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                                {this.state.element.customAttributes.map((item, index) => (
                                    <Tag key={index} value={item.label}/>
                                ))}
                            </div>
                        }
                    />}


                    {this.state.element.acceptedProviders && <DetailsInfoRow
                        label={I18n.t('ACCEPTED_PROVIDERS')}
                        value={
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                                {this.state.element.acceptedProviders.map((item, index) => (
                                    <Tag key={index} value={I18n.t(item + '_PROVIDER_DESCRIPTION')}/>
                                ))}
                            </div>
                        }
                    />}

                    <Box mt={4} textAlign="center">
                        <CodeQRCode value={this.state.element.url} size={128}/>
                        <Typography variant="caption" mt={1} display="block">{I18n.t("SCAN_TO_SHARE")}</Typography>
                    </Box>

                </div>}

            </Dialog>

        </div>);
    }
}

function mapStateToProps(state) {
    const locale = state.i18n.locale;
    const business = state.business;
    const permissions = state.auth.account?.data.permissions;
    const settings = state.settings;
    return {
        locale,
        business,
        permissions,
        settings
    };
}

export default connect(mapStateToProps)(PaymentLink);


